import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { PopupModal } from "react-calendly";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
	ArrowRight,
	CheckCircle,
	Ruler, // Icon for measuring/custom fit
	Palette, // Icon for style/samples
	PackageOpen, // Icon for delivery
	Star,
	ShieldCheck, // Icon for warranty/guarantee
	Users, // Icon for support
	MapPin, // Reiterate Canadian Made
	Truck, // Free Shipping
	MessageSquare, // For testimonials
} from "lucide-react";

import Hero from "../components/Hero"; // Keep importing the Hero
import PopupOffer from "../components/PopupOffer"; // Keep if still needed
import ProductQuiz from "../components/ProductQuiz";
import Slider from "react-slick";

// Reworked HomeScreen Component
const HomeScreen = () => {
	const [openCalendly, setOpenCalendly] = useState(false);
	// State for PopupOffer (keep if you still use it for specific promotions)
	const [showPopupOffer, setShowPopupOffer] = useState(false);
	const [forcePopupOffer, setForcePopupOffer] = useState(false);

	// Product Categories - Make this static and visual
	const productCategories = [
		{
			title: "Cellular Shades",
			url: "/images/newHome/shades/cellular.jpg", // Use clear category images
			to: "/shop/info/simply-cellular",
			description: "Energy efficient & stylish insulation.",
		},
		{
			title: "Roller Shades",
			url: "/images/newHome/shades/privacy_roller.jpg",
			to: "/shop/info/roller",
			description: "Sleek, modern light control.",
		},
		{
			title: "Zebra Shades",
			url: "/images/newHome/shades/privacy_zebra.jpg",
			to: "/shop/info/zebra",
			description: "Dual layers for versatile privacy.",
		},
		{
			title: "Sheer Shades",
			url: "/images/shadeinfo/shades/sheer/sheer.jpg",
			to: "/shop/info/sheer",
			description: "Softly filter light with elegance.",
		},
	];

	const testimonialSliderSettings = {
		dots: false, // Show navigation dots
		infinite: true, // Loop the slides
		speed: 500, // Transition speed
		slidesToShow: 3, // Number of slides visible at once on desktop
		slidesToScroll: 1, // How many slides to scroll at a time
		autoplay: true, // Enable autoplay
		autoplaySpeed: 2500, // Delay between slides
		pauseOnHover: true, // Pause autoplay on hover
		arrows: true, // Show prev/next arrows
		responsive: [
			// Adjust settings for different screen sizes
			{
				breakpoint: 1024, // Medium screens (tablets)
				settings: {
					slidesToShow: 2,
					slidesToScroll: 1,
				},
			},
			{
				breakpoint: 768, // Small screens (mobile)
				settings: {
					slidesToShow: 1,
					slidesToScroll: 1,
					arrows: false, // Hide arrows on mobile, rely on swipe/dots
				},
			},
		],
	};

	const testimonials = [
		{
			// Existing #1
			quote:
				"Absolutely love my new shades! The quality is top-notch and they fit perfectly. Highly recommend!",
			author: "Sarah M., Toronto ON",
			rating: 5,
		},
		{
			// Existing #2
			quote:
				"The free samples made choosing so easy. Installation was a breeze. Great value and service.",
			author: "David L., Vancouver BC",
			rating: 5,
		},
		{
			// Existing #3
			quote:
				"Fast shipping and excellent customer support. My living room looks completely transformed.",
			author: "Emily P., Calgary AB",
			rating: 5,
		},
		{
			// New #4 - Specific Product & Fit
			quote:
				"Ordered Zebra shades for my office. Measured exactly as the guide showed and the fit is flawless. They look incredibly modern.",
			author: "Michael B., Montreal QC",
			rating: 5,
		},
		{
			// New #5 - Quality & Value
			quote:
				"Was skeptical about ordering online, but the quality is fantastic, especially for the price. Better than big box stores!",
			author: "Jennifer S., Halifax NS",
			rating: 5,
		},
		{
			// New #6 - Ease of Installation
			quote:
				"Never installed blinds before, but the instructions were clear and simple. Had them up in under an hour.",
			author: "Chris T., Winnipeg MB",
			rating: 5, // Slightly different rating for variety
		},
		{
			// New #7 - Blackout Feature
			quote:
				"The blackout cellular shades are truly blackout! Perfect for my nursery. Finally, daytime naps are possible.",
			author: "Aisha K., Edmonton AB",
			rating: 5,
		},
		{
			// New #8 - Customer Service (Measurement Issue)
			quote:
				"I messed up my measurements slightly. Contacted support and they were incredibly helpful in getting it sorted out with minimal cost. Lifesavers!",
			author: "Ryan G., Ottawa ON",
			rating: 5,
		},
		{
			// New #9 - Speed
			quote:
				"Couldn't believe how fast they arrived! Custom made and shipped so quickly. Very impressed.",
			author: "Linda H., Saskatoon SK",
			rating: 5,
		},
		{
			// New #10 - Fabric Samples Accuracy
			quote:
				"The free samples matched the final product perfectly. Took the guesswork out of choosing the colour online.",
			author: "Kevin W., Mississauga ON",
			rating: 5,
		},
		{
			// New #11 - Sheer Shades Appearance
			quote:
				"My sheer shades are gorgeous! They let in so much soft light while still offering a touch of privacy during the day.",
			author: "Maria V., Quebec City QC",
			rating: 5,
		},
		{
			// New #12 - Repeat Customer
			quote:
				"This is my second order from Off Cut Shades. Consistent quality and service. Won't buy shades anywhere else.",
			author: "Peter J., Hamilton ON",
			rating: 5,
		},
		{
			// New #13 - Motorization
			quote:
				"Splurged on the motorized roller shades for the living room. So convenient and quiet! The app control is fantastic.",
			author: "Samantha D., Surrey BC",
			rating: 5,
		},
		{
			// New #14 - Website Experience
			quote:
				"The website customizer tool was easy to navigate and understand. Made ordering simple.",
			author: "Daniel C., London ON",
			rating: 5,
		},
		{
			// New #15 - Canadian Made Aspect
			quote:
				"Love that these are made in Canada! Happy to support local manufacturing while getting a great product.",
			author: "Chloe L., Victoria BC",
			rating: 5,
		},
		{
			// New #16 - Cellular Insulation
			quote:
				"Noticeable difference in room temperature since installing the cellular shades. They really help insulate.",
			author: "Mark R., St. John's NL",
			rating: 5,
		},
		{
			// New #17 - Zebra Functionality
			quote:
				"The versatility of the Zebra shades is amazing. Full privacy, filtered light, or open view - all in one.",
			author: "Isabelle F., Gatineau QC",
			rating: 5,
		},
		{
			// New #18 - Value Proposition
			quote:
				"Excellent value for custom shades. Shopped around and Off Cut had the best combination of price, quality, and speed.",
			author: "Ben P., Brampton ON",
			rating: 5,
		},
		{
			// New #19 - Shipping Packaging
			quote:
				"Shades arrived well-packaged and undamaged. Appreciated the careful shipping.",
			author: "Olivia N., Regina SK",
			rating: 5,
		},
		{
			// New #20 - Specific Need (Office Glare)
			quote:
				"Got the Solar shades for my home office to cut down on glare. They work perfectly and I can still see outside.",
			author: "Noah A., Vaughan ON",
			rating: 5,
		},
		{
			// New #21 - Color Choice Satisfaction
			quote:
				"The 'Charcoal' blackout fabric was exactly the dark grey I was looking for. Samples were spot on.",
			author: "Grace B., Kitchener ON",
			rating: 5,
		},
		{
			// New #22 - Ease for Large Windows
			quote:
				"Ordered a very wide shade for my patio door. The customization process handled the large size perfectly, and it operates smoothly.",
			author: "Ethan Z., Burnaby BC",
			rating: 5,
		},
		{
			// New #23 - Comparing to Professionals
			quote:
				"Felt like I got professionally fitted custom shades without the huge markup. Saved a lot doing it myself with their guides.",
			author: "Sophia T., Laval QC",
			rating: 5,
		},
		{
			// New #24 - Minor Issue Resolved
			quote:
				"Had a small issue with one bracket, customer service sent a replacement immediately. Great support.",
			author: "Liam G., Markham ON",
			rating: 5,
		},
		{
			// New #25 - Aesthetics Focus
			quote:
				"The clean lines of the roller shades updated my space instantly. Very happy with the minimalist look.",
			author: "Ava H., Halifax NS",
			rating: 5,
		},
		{
			// New #26 - Trust Factor Overcoming Hesitation
			quote:
				"Was hesitant to buy custom online, but the guarantees and positive reviews convinced me. Glad I did!",
			author: "Jackson K., Windsor ON",
			rating: 5,
		},
		{
			// New #27 - Sheer + Privacy Balance
			quote:
				"Needed something for the dining room that wasn't too heavy. The light filtering sheer shades are perfect - bright but not exposed.",
			author: "Isla P., Saskatoon SK",
			rating: 5,
		},
		{
			// New #28 - Simplicity Focus
			quote:
				"Just wanted simple, functional roller shades without too many decisions. The 'Easy Pick' concept (if applicable) or basic options were perfect.",
			author: "Lucas M., Sherbrooke QC",
			rating: 5,
		},
		{
			// New #29 - Return Customer for Different Room
			quote:
				"Got blackout for the bedroom first, now back for solar shades for the living room. Consistently good experience.",
			author: "Mia W., Richmond BC",
			rating: 5,
		},
		{
			// New #30 - Overall Satisfaction
			quote:
				"From samples to ordering to fast delivery and easy installation - the whole process was smooth. Highly satisfied.",
			author: "William R., St. Catharines ON",
			rating: 5,
		},
	];

	// Animation variants for sections
	const sectionVariants = {
		hidden: { opacity: 0, y: 40 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.6, ease: "easeOut" },
		},
	};

	return (
		<div className="overflow-x-hidden bg-white">
			<Helmet>
				{/* SEO Optimized Title & Description */}
				<title>
					Custom Window Shades Canada | Up to 50% Off | Off Cut Shades
				</title>
				<meta
					name="description"
					content="Shop custom window blinds & shades online. Save up to 50% on Cellular, Roller, Zebra & Sheer Shades. Handcrafted in Toronto. Free Samples & Shipping!"
				/>
				{/* Add other relevant meta tags */}
			</Helmet>

			{/* Optional Popup Offer */}
			<PopupOffer
				show={showPopupOffer}
				setShow={setShowPopupOffer}
				force={forcePopupOffer}
				setForce={setForcePopupOffer}
			/>

			{/* 1. Hero Section */}
			<Hero />

			{/* 2. Product Categories Section - VISUAL & ACTION-ORIENTED */}
			<motion.section
				className="py-16 sm:py-20 bg-gray-50"
				variants={sectionVariants}
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true }}
			>
				<div className="container mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
						Explore Our Custom Shades
					</h2>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
						{productCategories.map((category) => (
							<motion.div
								key={category.title}
								className="group relative rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300"
								whileHover={{ y: -5 }}
							>
								<Link to={category.to} className="block">
									<img
										src={category.url}
										alt={category.title}
										className="w-full h-48 sm:h-64 object-cover transition-transform duration-300 group-hover:scale-105"
									/>
									<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
									<div className="absolute bottom-0 left-0 p-4">
										<h3 className="text-lg sm:text-xl font-semibold text-white mb-1">
											{category.title}
										</h3>
										<p className="text-sm text-white/90 hidden sm:block">
											{category.description}
										</p>
										<span className="mt-2 inline-flex items-center text-white p-2 text-sm font-medium group-hover:underline bg-primary rounded-xl">
											Shop Now <ArrowRight className="ml-1 w-4 h-4" />
										</span>
									</div>
								</Link>
							</motion.div>
						))}
					</div>
					<div className="text-center mt-12">
						<Link
							to="/shop"
							className="inline-block bg-primary text-white px-8 py-3 rounded-lg font-semibold text-lg shadow-md hover:bg-primary/90 transition-all duration-300"
						>
							View All Products
						</Link>
					</div>
				</div>
			</motion.section>

			<motion.section
				className="py-16 sm:py-20 bg-gray-100" // Changed background for contrast
				variants={sectionVariants}
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true }}
			>
				<div className="mx-auto w-full">
					{" "}
					{/* Added container */}
					<h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
						{" "}
						{/* Adjusted margin */}
						Not Sure Where to Start?
					</h2>
					<p className="text-center text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
						{" "}
						{/* Added max-width and centering */}
						Answer a few quick questions, and we'll recommend the perfect shades
						for your needs.
					</p>
					<div className="w-full mx-auto">
						<ProductQuiz />
					</div>
				</div>
			</motion.section>

			{/* 3. How It Works Section - SIMPLIFY the process */}
			<motion.section
				className="py-16 sm:py-20"
				variants={sectionVariants}
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true }}
			>
				<div className="container mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
						Your Perfect Shades in 3 Simple Steps
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 text-center">
						{[
							{
								icon: <Palette className="w-10 h-10 mx-auto text-primary" />,
								title: "1. Explore & Sample",
								description:
									"Browse styles and order up to 5 free fabric samples to find your perfect match.",
							},
							{
								icon: <Ruler className="w-10 h-10 mx-auto text-primary" />,
								title: "2. Measure & Customize",
								description:
									"Easily measure your windows (we help!) and customize your shades online.",
							},
							{
								icon: (
									<PackageOpen className="w-10 h-10 mx-auto text-primary" />
								),
								title: "3. Order & Install",
								description:
									"Place your order and receive your custom, Canadian-made shades quickly.",
							},
						].map((step, index) => (
							<motion.div
								key={step.title}
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ delay: index * 0.1, duration: 0.5 }}
								viewport={{ once: true }}
								className="p-6"
							>
								<div className="mb-4">{step.icon}</div>
								<h3 className="text-xl font-semibold mb-2">{step.title}</h3>
								<p className="text-gray-600">{step.description}</p>
							</motion.div>
						))}
					</div>
					<div className="text-center mt-10">
						<Link
							to="/guides/measuring-guide" // Link to a dedicated measurement guide
							className="text-primary hover:underline font-medium"
						>
							Need help measuring? Check our guide{" "}
							<ArrowRight className="inline w-4 h-4" />
						</Link>
					</div>
				</div>
			</motion.section>

			{/* 4. Why Choose Off Cut Shades Section - Build TRUST & reinforce VALUE */}
			<motion.section
				className="py-16 sm:py-20 bg-gradient-to-b from-primary/5 to-white"
				variants={sectionVariants}
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true }}
			>
				<div className="container mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
						Quality & Service You Can Trust
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
						{[
							{
								icon: <MapPin className="w-8 h-8 text-primary" />,
								title: "Proudly Canadian Made",
								description:
									"Handcrafted with precision in our Toronto facility using quality materials.",
							},
							{
								icon: <Truck className="w-8 h-8 text-primary" />,
								title: "Fast, Free Shipping",
								description:
									"Enjoy complimentary shipping across Canada on all orders.",
							},
							{
								icon: <Users className="w-8 h-8 text-primary" />,
								title: "Expert Support",
								description:
									"Our dedicated team is here to help via chat, email, or phone.",
							},
							{
								icon: <ShieldCheck className="w-8 h-8 text-primary" />,
								title: "Satisfaction Guarantee",
								description:
									"We stand by our quality with a robust warranty and support.",
							}, // Add specifics if possible
						].map((feature) => (
							<div key={feature.title} className="flex items-start gap-4 p-4">
								<div className="mt-1 flex-shrink-0">{feature.icon}</div>
								<div>
									<h3 className="text-lg font-semibold mb-1">
										{feature.title}
									</h3>
									<p className="text-gray-600">{feature.description}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</motion.section>

			{/* 5. Customer Testimonials Section - SOCIAL PROOF */}
			<motion.section
				className="py-16 sm:py-20" // Adjust padding if needed
				variants={sectionVariants}
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true }}
			>
				<div className="container mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
						See What Our Customers Are Saying
					</h2>

					{/* Add some horizontal padding to container if arrows are outside */}
					<div className="relative px-6 sm:px-8 pb-10">
						<Slider {...testimonialSliderSettings}>
							{testimonials.map((testimonial, index) => (
								// Each testimonial needs padding *within* the slide
								<div key={index} className="px-2 sm:px-3 h-full">
									{/* The original motion.div acts as the slide content */}
									<motion.div
										// key is handled by the parent div now
										className="bg-gray-50 p-6 rounded-lg shadow-sm h-full flex flex-col" // Ensure full height and flex for alignment
									>
										{/* Stars */}
										<div className="flex items-center mb-3 flex-shrink-0">
											{" "}
											{/* Added flex-shrink-0 */}
											{[...Array(testimonial.rating)].map((_, i) => (
												<Star
													key={i}
													className="w-4 h-4 text-yellow-400 fill-current"
												/> // Slightly smaller stars?
											))}
											{[...Array(5 - testimonial.rating)].map((_, i) => (
												<Star
													key={i + testimonial.rating}
													className="w-4 h-4 text-gray-300"
												/>
											))}
										</div>
										{/* Quote */}
										<p className="text-gray-700 italic mb-4 text-sm leading-relaxed flex-grow">
											{" "}
											{/* Added text-sm, leading-relaxed, flex-grow */}"
											{testimonial.quote}"
										</p>
										{/* Author */}
										<p className="font-semibold text-gray-800 text-sm mt-auto flex-shrink-0">
											{" "}
											{/* Added mt-auto, flex-shrink-0 */}- {testimonial.author}
										</p>
									</motion.div>
								</div>
							))}
						</Slider>
					</div>
				</div>
			</motion.section>

			{/* 6. Sample Request Section - Strong CTA, simplified design */}
			<motion.section
				className="py-16 sm:py-20 bg-secondary text-white" // Use a brand color
				variants={sectionVariants}
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true }}
			>
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
					<h2 className="text-3xl md:text-4xl font-bold mb-4">
						Find Your Perfect Fabric Match
					</h2>
					<p className="text-lg md:text-xl mb-8 text-white/90 max-w-2xl mx-auto">
						Not sure which color or material? Order up to 5 free samples
						delivered right to your door.
					</p>
					<motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
						<Link
							to="/swatches"
							className="inline-flex items-center px-10 py-3 bg-white text-secondary rounded-lg font-semibold text-lg shadow-md group transition-colors hover:bg-gray-100"
						>
							Request Free Samples
							<ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
						</Link>
					</motion.div>
				</div>
			</motion.section>

			{/* 7. Consultation / Help Section - Reassure and offer help */}
			<section className="py-16 sm:py-20">
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
					<h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
						Need Expert Advice?
					</h2>
					<p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
						Whether you need help measuring, choosing styles, or have any
						questions, our experts are ready to assist.
					</p>
					<motion.button
						onClick={() => setOpenCalendly(true)}
						className="inline-block bg-primary text-white px-8 py-3 rounded-lg font-semibold text-lg shadow-md hover:bg-primary/90 transition-all duration-300"
						whileHover={{ scale: 1.05 }}
						whileTap={{ scale: 0.95 }}
					>
						Book a Free Consultation
					</motion.button>
					{/* Add links to Contact Us or FAQ pages */}
					<p className="mt-6 text-gray-600">
						Or visit our{" "}
						<Link to="/help/faq" className="text-primary hover:underline">
							FAQ
						</Link>{" "}
						for quick answers.
					</p>
				</div>
			</section>

			{/* Calendly Popup Modal */}
			<PopupModal
				url="https://calendly.com/offcutshades"
				rootElement={document.getElementById("root")}
				open={openCalendly}
				onModalClose={() => setOpenCalendly(false)}
			/>
		</div>
	);
};

export default HomeScreen;
